<template>
  <BaseTemplate :currentUser="currentUser">
    <div class="my-page">
      <Title jpn="My page" />
      <div class="base_area">
        <div class="col">
          <GrayBack class="gray_area">
            <div class="name_area">
              <span>{{ lastName }} {{ firstName }}</span>
            </div>
          </GrayBack>
          <h4>Your points</h4>
          <h5>{{ point | comma }}P</h5>
          <div class="button_area">
            <SquareButton
              class="square-button"
              text="Buy points"
              @handleSquareBtnClick="goToPointBuy"
            />
          </div>
          <BarGraph
            :maxValue="rankNextPrice"
            :currentValue="rankCurrentPrice"
            :rankName="rankName"
            barColor="#20366F"
          />
          <div v-if="isRankMaintained" class="button_area">
            <BlackGradationButton
              class="square-button"
              text="Maintains rank next month"
              @handleSquareBtnClick="clickMaintainRank"
            />
          </div>
        </div>
        <div class="col">
          <div class="link_area">
            <GrayBack class="gray_area">
              <div class="link" @click="goToPointHistory">Your points history</div>
            </GrayBack>
            <GrayBack class="gray_area">
              <div class="link" @click="goToBuyProductHistory">Buying history</div>
            </GrayBack>
            <GrayBack class="gray_area">
              <div class="link" @click="goToUserGuide">User guide</div>
            </GrayBack>
            <GrayBack class="gray_area">
              <div class="link" @click="goContactUs">Contact us</div>
            </GrayBack>
            <GrayBack class="gray_area">
              <div class="link" @click="goToUserMenu">Setting registration information</div>
            </GrayBack>
          </div>
          <div class="button_area button_logout">
            <BlackGradationButton
              class="square-button"
              text="Logout"
              img="/img/logout_icon.png"
              @handleSquareBtnClick="logout"
            />
          </div>
        </div>
      </div>
    </div>
    <CommonOverlay class="overlay" v-show="isOpenConfirm && isRankMaintained">
      <CommonModal class="modal pack-confirm">
        <span class="close" @click="isOpenConfirm = false">&times;</span>
        <div class="modal-title">
          <span>Maintain rank next month</span>
        </div>
        <div class="bay_area">
          <div class="modal-subtitle">
            <span>Next month's start rank</span>
          </div>
          <div class="use_num">
            <span>{{ rankNextMonthName }}</span>
          </div>
        </div>
        <div class="bay_area">
          <div class="modal-subtitle">
            <span>Use coin</span>
          </div>
          <div class="use_num">
            <span>{{ rankCoin | comma }}C</span>
          </div>
          <div class="modal-subtitle">
            <span>Coins held</span>
          </div>
          <div class="use-point">
            <div class="point-area">{{ currentUser.coin | commaFloat }}C</div>
            <div>→</div>
            <div class="point-area">{{ currentUser.coin - rankCoin | commaFloat }}C</div>
          </div>
        </div>
        <div class="modal-button-area">
          <BlackGradationButton class="bay-button" text="Maintain rank next month" @handleSquareBtnClick="buyRank" />
        </div>
      </CommonModal>
    </CommonOverlay>
    <CommonOverlay class="overlay" v-show="isOpenConp">
      <CommonModal class="modal pack-confirm">
        <span class="close" @click="isOpenConp = false">&times;</span>
        <div class="modal-title">
          <span>Maintain rank next month</span>
        </div>
        <div class="modal-title">
          <span>Maintained the starting rank for next month to {{ rankNextMonthName }}.</span>
        </div>
        <div class="modal-button-area">
          <BlackGradationButton class="bay-button" text="閉じる" @handleSquareBtnClick="isOpenConp = false" />
        </div>
      </CommonModal>
    </CommonOverlay>
  </BaseTemplate>
</template>

<script>
import { mapGetters } from 'vuex';
import ClickOutside from 'vue-click-outside';
import {
  separateCharacterNumber,
  separateDecimalNumber,
} from '@/common/util/format';

import BaseTemplate from '@/components/templates/BaseTemplate.vue';
import GrayBack from '@/components/molecules/GrayBack.vue';
import BlackGradationButton from '@/components/atoms/BlackGradationButton.vue';
import SquareButton from '@/components/atoms/SquareButton.vue';
import Title from '@/components/molecules/Title.vue';
import t from '@/plugins/i18n';
import BarGraph from '@/components/atoms/BarGraph.vue';
import CommonOverlay from '@/components/atoms/CommonOverlay.vue';
import CommonModal from '@/components/atoms/CommonModal.vue';

export default {
  name: 'MyPage',
  mixins: [t],

  components: {
    BaseTemplate,
    GrayBack,
    BlackGradationButton,
    SquareButton,
    Title,
    BarGraph,
    CommonOverlay,
    CommonModal,
  },

  directives: {
    ClickOutside,
  },

  filters: {
    comma(value) {
      return separateCharacterNumber(parseInt(value, 10));
    },
    commaFloat(value) {
      return separateDecimalNumber(value, true);
    },
  },

  data: () => ({
    lastName: null,
    firstName: null,
    point: null,
    rankName: null,
    rankNextPrice: 0,
    rankCurrentPrice: 0,
    rankCoin: 0,
    rankNextMonthName: '',
    rankNextMonth: 1,
    isRankMaintained: false,
    isOpenConfirm: false,
    isOpenConp: false,
  }),

  computed: {
    ...mapGetters({
      currentUser: 'user/currentUser',
      currentUserDetail: 'user/currentUserDetail',
      currentAddress: 'zipCode/currentAddress',
      changeUserInformationResult: 'user/changeUserInformationResult',
      logOutUserResult: 'user/logOutUserResult',
      buyRankResult: 'user/buyRankResult',
    }),
  },

  watch: {
    currentUserDetail(newResult) {
      this.$nextTick(() => {
        this.lastName = newResult.lastName;
        this.firstName = newResult.firstName;
        this.point = newResult.availablePoint;
        this.rankName = newResult.rank_name;
        this.rankCoin = newResult.rank_coin;
        this.rankNextPrice = newResult.rank_max_price;
        this.rankCurrentPrice = newResult.rank_current_price;
        this.isRankMaintained = newResult.is_rank_maintained;
        this.rankNextMonthName = newResult.next_month_name;
        this.rankNextMonth = newResult.next_month;
      });
    },
    logOutUserResult(newVal) {
      if (newVal) {
        if (newVal.state === 200) {
          this.$store.dispatch('user/clearLogOutUserResult');
          this.$router.push('/login');
        }
      }
    },
  },

  created() {
    this.$store.dispatch('user/setCurrentUserDetail');
  },

  methods: {
    logout() {
      this.$store.dispatch('user/logOutUser');
    },
    goToUserMenu() {
      this.$router.push('./user-menu');
    },
    goToPointBuy() {
      this.$router.push('./buy-point');
    },
    goToBuyProductHistory() {
      this.$router.push('/buy-product-history');
    },
    goToPointHistory() {
      this.$router.push('./buy-point-history');
    },
    goToUserGuide() {
      this.$router.push('/user-guide');
    },
    goContactUs() {
      if (process.env.VUE_APP_LANG_MODE === 'jp') {
        window.open('https://minny-online-store.iranoan.com/contacts?service=1', '_blank');
      } else {
        window.open('https://minny-online-store.iranoan.com/contacts?service=1', '_blank');
      }
    },
    clickMaintainRank() {
      this.$store.dispatch('user/setCurrentUserDetail');
      if (this.currentUser.coin < this.rankCoin) {
        window.alert('コインが不足しています');
        return;
      }
      this.isOpenConfirm = true;
    },
    buyRank() {
      (async () => {
        this.isOpenConfirm = false;
        const postParams = {
          coin: this.rankCoin,
          next_month_rank: this.rankNextMonth,
        };
        await this.$store.dispatch('user/buyRank', { postParams });
        const { buyRankResult } = this;
        const isSuceess = buyRankResult.state === 200 && buyRankResult.result === '1';
        if (isSuceess) {
          this.isOpenConp = true;
          this.$store.dispatch('user/setCurrentUser');
          this.$store.dispatch('user/setCurrentUserDetail');
        } else {
          const { count } = buyRankResult.errors;
          window.alert(count);
        }
      })();
    },
  },
};
</script>

<style lang="scss" scoped>
.my-page{
  width: 100%;
  max-width: 800px;
  padding-top: 30px;
  padding-bottom: 50px;
  margin: 0px auto;
  @include appendMap($backgroundStyleMap);
  .address_change {
    margin-top: 5px;
  }
  .col {
    flex-basis: 0;
    flex-grow: 1;
    max-width: 100%;
    width: 100%;
    padding: 12px;
  }
  .base_area {
    display: flex;
    .gray_area {
      width: 100%;
      background-color: $lightColor;
    }
    .name_area {
      color: $white;
      width: 100%;
      text-align: center;
      font-size: 24px;
    }
    h4 {
      margin: 40px 40px 10px 40px;
      background-color: $white;
      color: $deepColor;
      text-align: center;
      font-weight: normal;
    }
    h5 {
      text-align: center;
      color: $white;
      font-size: 36px;
    }
    .button_area {
      margin-top: 20px;
      .square-button {
        max-width: 300px;
        margin: auto;
      }
    }
    .link_area {
      .gray_area {
        padding: 10px 0;
        text-align: center;
        color: $white;
        margin: 5px 0;
        cursor: pointer;
        transition: .4s;
        &:nth-child(1) {
          margin-top: 0;
        }
        &:hover {
          background-color: $mainColor;
        }
      }
    }
    .button_logout {
      margin-top: 40px;
      .square-button {
        max-width: 200px;
      }
    }
  }
}
.overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0px 30px;

  .modal {
    padding: 40px 28px;
    font-family: $notoSans;
    color: #000;
    text-align: center;
    width: 586px;

    .modal-title {
      font-size: 24px;
      font-weight: bold;
    }

    .text {
      display: flex;
      flex-direction: column;
      margin-top: 20px;
      font-size: 16px;
      font-weight: 400;
      line-height: 28px;
    }

    .modal-button-area {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 30px;

      .square-button {
        width: 120px;
        height: 50px;

        &+.square-button {
          margin-left: 20px;
        }
      }
    }
  }
}
.pack-confirm {
  background-color: #fff;
  color: #000;
  position: absolute;
  height: auto;

  .close {
    /* 閉じるボタンを右上に表示 */
    position: absolute;
    top: 10px;
    right: 10px;
    /* 閉じるボタンの大きさ */
    font-size: 24px;
    /* 閉じるボタンのカーソル */
    cursor: pointer;
  }

  .bay_area {
    background-color: $lightGray;
    padding: 20px 0;
    border-radius: 9px;
    & + .bay_area {
      margin-top: 10px;
    }
  }

  .modal-subtitle {
    width: 50%;
    margin: auto;
    background-color: $mainColor;
    color: #fff;
  }

  .use_num {
    font-size: 40px;
    font-weight: bold;
    color: #000;
  }

  .use-point {
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 10px 0;
    width: 50%;
    margin: auto;
  }

  .bay-button {
    width: 100%;
    height: 52px;
  }
}
@media screen and (max-width: $tabletWidth) {
  .my-page{
    .base_area {
      display: flex;
      flex-direction: column;
    }
  }
  .overlay {
    .modal {
      width: 80%;
    }
  }
  .pack-confirm {
    .modal-subtitle {
      font-size: 12px;
    }

    .use_num {
      font-size: 20px;
    }

    .bay-button {
      height: 45px;
    }
  }
}
</style>
